import { Injectable } from "@angular/core";
import {
  AngularFireDatabase,
  AngularFireObject,
  AngularFireList,
  SnapshotAction,
} from "@angular/fire/compat/database";
import { BehaviorSubject, Observable } from "rxjs";
import { first, forkJoin, firstValueFrom } from "rxjs";
import { map, switchMap } from "rxjs/operators";
import { EMPTY } from "rxjs";

import { DataProvider, SyncData } from "./data-provider.d";

import { console } from "#/log";
import type { SamplesProgress, UserBuild } from "./data.service";

/**
 * Remote Data Service
 * Handles all storage and retrieval to and from remote storage (firebase)
 */

@Injectable()
export class RemoteDataService implements DataProvider {
  protected userId: string | null = null;

  private connectionStatus: Observable<any> = EMPTY; // Default to EMPTY observable
  private currentBuild: Observable<UserBuild> = EMPTY;
  private currentRanges: Observable<any> = EMPTY;
  private currentNotes: Observable<any> = EMPTY;
  private currentUser: Observable<any> = EMPTY;
  private samplesProgress: Observable<SamplesProgress> = EMPTY;

  private currentBuildWritable: AngularFireObject<any> =
    {} as AngularFireObject<any>;
  private currentRangesWritable: AngularFireList<any> =
    {} as AngularFireList<any>;
  private currentNotesWritable: AngularFireObject<any> =
    {} as AngularFireObject<any>;
  private currentUserWritable: AngularFireObject<any> =
    {} as AngularFireObject<any>;
  private samplesProgressWritable: AngularFireObject<SamplesProgress> =
    {} as AngularFireObject<SamplesProgress>;

  private lastUpdate = -1;
  private availableSubject: BehaviorSubject<boolean> = new BehaviorSubject(
    false,
  );
  private subscriptions: Array<any> = [];

  constructor(private af: AngularFireDatabase) {
    this.watchConnectionStatus();
  }

  /**
   * Returns the availability of the service.
   */
  isAvailable(): boolean {
    return this.availableSubject.getValue();
  }

  /**
   * Returns an observable for monitoring the availability of the provider
   */
  availableStatus(): Observable<boolean> {
    return this.availableSubject.asObservable();
  }

  /**
   * Returns an observable of the connection status
   */
  getConnection(): Observable<any> {
    return this.connectionStatus;
  }

  /**
   * Returns a timestamp of the last update to the service or returns -1
   * if the remote storage hasn't initialized
   */
  getLastUpdated(): number {
    return this.lastUpdate;
  }

  /**
   * Returns the current build Observable
   */
  getCurrentBuild(): Observable<any> {
    return this.currentBuild;
  }

  /**
   * Returns the current Range list observable
   */
  getCurrentRanges(): Observable<any> {
    return this.currentRanges;
  }

  /**
   * Returns the current notes Observable
   */
  getCurrentNotes(): Observable<any> {
    return this.currentNotes;
  }

  /**
   * Returns an observable for the progress of all samples.
   * If RemoteDataService is unavailable, redirect to EMPTY; this allows
   * subscriptions to continue updating across availability changes.
   */
  getSamplesProgress(): Observable<SamplesProgress> {
    return this.availableStatus().pipe(
      // tap((available) =>
      //   console.debug(
      //     `remote-data: getSamplesProgress switchMap: available:`,
      //     available,
      //   ),
      // ),
      switchMap((available) => {
        return available ? this.samplesProgress : EMPTY;
      }),
    );
  }

  /**
   * Gets all of the user data from the remote storage and resolves it in
   * a promise.
   */
  async getSyncData(): Promise<SyncData> {
    this.assertUserId();
    let notes = this.fetchObject<SyncData["notes"]>(
      "/user-notes/" + this.userId,
    ).pipe(first());
    let ranges = this.fetchObject<SyncData["ranges"]>(
      "/user-ranges/" + this.userId,
    ).pipe(first());
    let builds = this.fetchObject<SyncData["builds"]>(
      "/user-builds/" + this.userId,
    ).pipe(first());
    let progress = this.fetchObject<SyncData["progress"]>(
      "/users/" + this.userId + "/samples",
    ).pipe(first());

    try {
      const data = await firstValueFrom(
        forkJoin([progress, ranges, builds, notes]),
      );
      if (!data) {
        throw new Error("No data received from forkJoin");
      }
      const syncData = {
        progress: data[0],
        ranges: data[1],
        builds: data[2],
        notes: data[3],
      };

      if (data.some((d) => null)) {
        throw new Error(
          `Nulls in query results from forkJoin: ${JSON.stringify({ syncData })}`,
        );
      }
      return syncData;
    } catch (error) {
      if (error instanceof Error) {
        error.message = "Error fetching sync data: " + error.message;
      } else {
        console.error("Error fetching sync data:", error);
      }
      throw error;
    }
  }

  /**
   * Sends a data dump from the `newData` object to remote storage.
   */
  async setSyncData(newData: SyncData, timestamp: number): Promise<void> {
    console.debug("remote-data: Got sync data", newData);
    await Promise.all([
      this.fetchWriteObject("/user-notes/" + this.userId).set(newData.notes),
      this.fetchWriteObject("/user-ranges/" + this.userId).set(newData.ranges),
      this.fetchWriteObject("/user-builds/" + this.userId).set(newData.builds),
      this.fetchWriteObject("/users/" + this.userId + "/samples").set(
        newData.progress,
      ),
      this.setLastUpdated(timestamp),
    ]);
  }

  /**
   * Sets the last time there was an update to the remote data
   */
  setLastUpdated(timestamp: number): PromiseLike<void> {
    return this.currentUserWritable.update({ lastUpdate: timestamp });
  }

  /**
   * Sets the current user and the progress observable, once the current user
   * is available, the remote data is set as "available" because it can store
   * and retreive user data. If a null UID is passed, the subscriptions are
   * cancelled as the user is no longer logged in.
   */
  setUserId(uid: string | null): void {
    console.debug(`remote-data: setUserId: [${typeof uid}]:`, uid);
    this.userId = uid;
    if (this.userId) {
      console.debug(
        `remote-data.service: set uid '${this.userId}'; initializing`,
      );
      this.initializeProgressObservable();
      [this.currentUser, this.currentUserWritable] =
        this.fetchObjectAndWritable("users");
      this.subscriptions.push(
        this.currentUser.subscribe((userData) => {
          if (userData) {
            if (userData.lastUpdate) {
              this.lastUpdate = userData.lastUpdate;
            }
            if (!this.isAvailable()) {
              this.availableSubject.next(true);
            }
          }
        }),
      );
    } else {
      console.debug(
        `remote-data.service: empty/null uid '${uid}'; clearing state`,
      );
      this.clearProgressObservable();
      this.subscriptions.forEach((s) => {
        s.unsubscribe();
      });
      this.subscriptions = [];
    }
  }

  /**
   * Check that this.userId is defined; guard against accidentally referencing null userId
   */
  protected assertUserId(): asserts this is typeof this & { userId: string } {
    if (!this.userId || !this.userId.length)
      throw new Error(
        `remote-data.service: expected valid userId, got '${JSON.stringify(this.userId)}'`,
      );
  }

  /**
   * Sets the progress for the sample with the corresponding `sampleId`
   */
  async setSampleProgress(sampleId: string, progress: number) {
    let updateValue: Record<string, { progress: number }> = {
      [sampleId]: { progress },
    };

    console.debug(`remote-data: setSampleProgress:`, updateValue);
    await this.samplesProgressWritable.update(updateValue);
    console.debug(`remote-data: setSampleProgress`, updateValue, "done");
  }

  /**
   * Sets the appropriate achievement progress
   * @param achievementId id of the achievement to set
   * @param progress progress to set
   */
  setAchievementProgress(achievementId: string, progress: number) {
    //be careful not to use just id, thats what samples are using
    /* coming soon
    let updateValue = {};
    updateValue["a_"+achievementId] = { progress: progress };
    this.samplesProgressWritable.update(updateValue);*/
  }

  /**
   * Submit feedback to Firebase
   */
  sendFeedback(feedbackData: Object) {
    console.debug("remote-data: Got this feedback data to send", feedbackData);
    this.assertUserId();
    return this.fetchWriteList("/user-feedback/" + this.userId).push(
      feedbackData,
    );
  }

  /**
   * Initialize observables for the passed sample
   */
  setCurrentSample(sampleId: string | null) {
    if (sampleId) {
      this.assertUserId();

      [this.currentBuild, this.currentBuildWritable] =
        this.fetchObjectAndWritable("user-builds", { subpath: sampleId });

      [this.currentRanges, this.currentRangesWritable] =
        this.fetchObjectAndWritable("user-ranges", {
          subpath: sampleId,
          asList: true,
        });

      [this.currentNotes, this.currentNotesWritable] =
        this.fetchObjectAndWritable("user-notes", { subpath: sampleId });
    } else {
      this.currentBuild = EMPTY;
      this.currentRanges = EMPTY;
      this.currentNotes = EMPTY;
      /**
       * It's possible that a null sample will get set before a proper sample,
       * in which case the currentXyzWritables will not have been initialized
       **/
      this.currentBuildWritable.remove?.();
      this.currentRangesWritable.remove?.();
      this.currentNotesWritable.remove?.();
    }
  }

  /**
   * Sets the build in remote storage
   */
  async setBuild(newData: UserBuild) {
    await this.currentBuildWritable.set(newData);
  }

  /**
   * Sets the range with the corresponding `rangeId` in remote storage
   */
  async setRange(rangeId: string, newData: Object): Promise<void> {
    await this.currentRangesWritable.remove(rangeId);
    await this.updateRange(rangeId, newData);
  }

  /**
   * Sets the notes in remote storage
   */
  async setNotes(newData: Object): Promise<void> {
    await this.currentNotesWritable.set(newData);
  }

  /**
   * Update the build in remote storage
   */
  async updateBuild(newData: Partial<UserBuild>): Promise<void> {
    await this.currentBuildWritable.update(newData);
  }

  /**
   * Update the range with the provided `rangeId` in remote storage
   */
  async updateRange(rangeId: string, newData: Object) {
    await this.currentRangesWritable.update(rangeId, newData);
  }

  /**
   * Reset all of the user data in remote storage
   */
  async reset() {
    console.debug("remote-data: resetting");
    const DATA_EMPTY = {};

    await Promise.all([
      this.fetchWriteObject("/user-builds/" + this.userId).set(DATA_EMPTY),
      this.fetchWriteObject("/user-ranges/" + this.userId).set(DATA_EMPTY),
      this.fetchWriteObject("/user-notes/" + this.userId).set(DATA_EMPTY),
      this.samplesProgressWritable.remove(),
    ]);
  }

  /**
   * Fetches connection info from firebase and subscribes so it can set the
   * availability status of the remote data service. The remote data service
   * lives for the entire lifespan of the app, so the subscription never ends.
   */
  private watchConnectionStatus() {
    this.connectionStatus = this.fetchObject(".info/connected");
    this.connectionStatus.subscribe((connected) => {
      console.debug("remote-data: connected", connected);
      if (connected.$value && this.lastUpdate > 0 && this.userId) {
        this.availableSubject.next(true);
      } else if (!connected.$value) {
        this.availableSubject.next(false);
      }
    });
  }

  /**
   * Sets the observable and writer for tracking sample progress
   */
  private initializeProgressObservable() {
    console.debug("remote-data: fetching samples progress");
    this.assertUserId();

    [this.samplesProgress, this.samplesProgressWritable] =
      this.fetchObjectAndWritable("users", { subpath: "samples" });
  }

  /**
   * Unset observable and writer for tracking sample progress
   */
  private async clearProgressObservable() {
    this.samplesProgress = EMPTY;
    await this.samplesProgressWritable.remove();
  }

  private fetchObjectAndWritable<T>(
    scope: string,
    options?: {
      subpath?: string | Array<string>;
      asList?: false;
    },
  ): readonly [object: Observable<T>, writable: AngularFireObject<T>];
  private fetchObjectAndWritable<T>(
    scope: string,
    options: {
      subpath?: string | Array<string>;
      asList: true;
    },
  ): readonly [
    list: Observable<(string | null)[]>,
    writable: AngularFireList<T>,
  ];
  /**
   * Helper to initialize both observable and writable for data objects
   * at specific path in the datastore
   **/
  private fetchObjectAndWritable<T>(
    /** Top-level scoping path (e.g. "user", "user-builds") */
    scope: string,
    options?: {
      /** Append to the data path (e.g. sample ID) */
      subpath?: string | Array<string>;
      /** Fetch as list (@see {@link fetchList}, {@link fetchWriteList}) rather than object */
      asList?: boolean;
    },
  ):
    | readonly [object: Observable<T>, writable: AngularFireObject<T>]
    | readonly [
        list: Observable<(string | null)[]>,
        writable: AngularFireList<T>,
      ] {
    this.assertUserId();

    const path = [
      /* to create root leading / */
      "",
      scope,
      this.userId,
    ];

    if (options?.subpath) {
      path.push(
        ...(Array.isArray(options.subpath)
          ? options.subpath
          : [options.subpath]),
      );
    }

    const key = path.join("/");

    if (options?.asList) {
      return [this.fetchList(key), this.fetchWriteList<T>(key)] as const;
    } else {
      return [this.fetchObject<T>(key), this.fetchWriteObject<T>(key)] as const;
    }
  }

  /**
   * Helper function to fetch an ObjectObservable object from remote storage.
   * Simulates angularfire 4.0.0 behaviour
   */
  private fetchObject<T>(objectPath: string): Observable<T> {
    return this.fetchWriteObject<T>(objectPath)
      .snapshotChanges()
      .pipe(
        map((item: SnapshotAction<any>) => {
          if (typeof item.payload.val() !== "object") {
            return { $key: item.key, $value: item.payload.val() };
          }
          return { $key: item.key, ...item.payload.val() };
        }),
      );
  }

  /**
   * Returns a bare firebase object for manipulation
   * @param objectPath Path to the object
   */
  private fetchWriteObject<T>(objectPath: string): AngularFireObject<T> {
    return this.af.object<T>(objectPath);
  }

  /**
   * Helper function to fetch a ListObservable from remote storage
   */
  private fetchList(listPath: string): Observable<(string | null)[]> {
    return this.fetchWriteList(listPath)
      .snapshotChanges()
      .pipe(
        map((actions) =>
          actions
            .map((action) => {
              const val = action.payload.val();
              // Check if the value is an object before spreading
              return {
                $key: action.key,
                ...(val && typeof val === "object" ? val : {}),
              };
            })
            .map((item) => item.$key),
        ),
      );
  }

  /**
   * Returns a bare firebase list for manipulation
   * @param objectPath Path to the list
   */
  private fetchWriteList<T>(listPath: string): AngularFireList<T> {
    return this.af.list<T>(listPath);
  }
}
